<template>
  <div
    class="frisbuy-review-author"
    :class="{ 'frisbuy-review-author--popup': props.view === 'popup' }"
  >
    <template v-if="props.view === 'popup'">
      <div class="frisbuy-review-author__main">
        <div class="frisbuy-review-author__avatar" :style="avatarStyle">
          {{ customerInitial }}
        </div>

        <div class="frisbuy-review-author__meta">
          <div class="frisbuy-review-author__name">
            {{ customerNamePopup }}
          </div>
          <div class="frisbuy-review-author__date">{{ props.reviewDate }}</div>
        </div>
      </div>

      <div v-if="props.rating" class="frisbuy-review-author__rating-simple">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.5475 9.00373L14.3506 8.38782L12.4744 4.54711C12.4232 4.44196 12.3389 4.35683 12.2348 4.30509C11.9736 4.17489 11.6562 4.28339 11.5256 4.54711L9.64949 8.38782L5.45256 9.00373C5.33685 9.02043 5.23106 9.07551 5.15007 9.15897C5.05215 9.26059 4.99819 9.39732 5.00005 9.5391C5.00191 9.68088 5.05943 9.81611 5.15998 9.91509L8.19652 12.9045L7.47912 17.1258C7.4623 17.224 7.47306 17.325 7.51018 17.4173C7.54731 17.5097 7.60931 17.5896 7.68916 17.6482C7.76901 17.7068 7.86352 17.7416 7.96196 17.7486C8.0604 17.7557 8.15883 17.7348 8.24611 17.6883L12 15.6953L15.754 17.6883C15.8564 17.7434 15.9755 17.7617 16.0895 17.7417C16.3771 17.6916 16.5705 17.4162 16.5209 17.1258L15.8036 12.9045L18.8401 9.91509C18.9227 9.8333 18.9773 9.72647 18.9938 9.60963C19.0384 9.31753 18.8368 9.04713 18.5475 9.00373Z"
            fill="#212121"
          />
        </svg>
        <span class="frisbuy-review-author__rating-value">{{
          props.rating
        }}</span>
      </div>

      <Vue3StarRating
        v-if="props.rating"
        class="frisbuy-review-author__rating-mobile-stars"
        :modelValue="props.rating"
        :numberOfStars="5"
        :starSize="16"
        starColor="#FFFFFF"
        inactiveColor="rgba(255, 255, 255, 0.4)"
        :disableClick="true"
        :customSvg="props.isSoftStarIcon ? StarSoftRounded : StarRounded16"
      />
    </template>

    <template v-else>
      <div class="frisbuy-review-author__customer-info">
        <div
          class="frisbuy-review-author__name frisbuy-review-author__name--default"
        >
          {{ customerNameDefault }}
        </div>

        <Vue3StarRating
          v-if="props.rating"
          :modelValue="props.rating"
          :numberOfStars="5"
          :starSize="12"
          starColor="var(--frisbuy-color-primary)"
          inactiveColor="var(--frisbuy-color-primary-2)"
          :disableClick="true"
          :customSvg="props.isSoftStarIcon ? StarSoftRounded : StarRounded"
        />
      </div>

      <div class="frisbuy-review-author__source">
        <div class="frisbuy-review-author__date">
          <time :datetime="props.reviewDate">
            {{ props.reviewDate }}
          </time>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Vue3StarRating from "vue3-star-ratings";
import StarSoftRounded from "../Icons/ReviewAuthorStarSoftRounded.vue";
import StarRounded from "../Icons/ReviewAuthorStarRounded.vue";
import StarRounded16 from "../Icons/ReviewAuthorStarRounded16.vue";

const props = withDefaults(
  defineProps<{
    customerName: string;
    reviewDate: string;
    rating?: number;
    isSoftStarIcon?: boolean;
    avatarColorIndex?: number;
    view?: "default" | "popup";
  }>(),
  {
    view: "default",
  }
);

const customerInitial = computed(() => {
  const name = props.customerName?.trim() || "";
  return (name[0] || "A").toUpperCase();
});

const customerNamePopup = computed(() => {
  const name = props.customerName?.trim() || "";
  if (name.length <= 16) return name;
  return `${name.slice(0, 16)}...`;
});

const customerNameDefault = computed(() => {
  const name = props.customerName?.trim() || "";
  if (name.length <= 27) return name;
  return `${name.slice(0, 27)}...`;
});

const avatarColors = [
  "rgba(188, 200, 210, 1)",
  "rgba(114, 207, 154, 1)",
  "rgba(158, 204, 246, 1)",
  "rgba(127, 131, 234, 1)",
  "rgba(121, 85, 72, 1)",
];

const avatarStyle = computed(() => {
  const index = props.avatarColorIndex ?? 1;
  const normalizedColorIndex = (Math.max(1, index) - 1) % avatarColors.length;

  return {
    background: avatarColors[normalizedColorIndex],
  };
});
</script>

<style scoped lang="scss">
.frisbuy-review-author {
  display: flex;
  flex-direction: column;
}

.frisbuy-review-author__customer-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  :deep(.vue3-star-ratings__icon) {
    margin-left: 0 !important;
  }

  :deep(.vue3-star-ratings__icon + .vue3-star-ratings__icon) {
    margin-left: 4px !important;
  }
}

.frisbuy-review-author__name--default {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #212121;
}

.frisbuy-review-author__source {
  margin-top: 0;
}

.frisbuy-review-author__date {
  display: inline-block;
  width: fit-content;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--frisbuy-color-primary-3);
}

.frisbuy-review-author--popup {
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.frisbuy-review-author__main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.frisbuy-review-author--popup .frisbuy-review-author__main {
  flex: 1 1 auto;
  min-width: 0;
}

.frisbuy-review-author__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  min-width: 48px;
  height: 48px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 32px;
  font-weight: 400;
  line-height: 1;
}

.frisbuy-review-author__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.frisbuy-review-author--popup .frisbuy-review-author__meta {
  min-width: 0;
}

.frisbuy-review-author--popup .frisbuy-review-author__name {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.frisbuy-review-author--popup .frisbuy-review-author__date {
  font-size: 12px;
  line-height: 16px;
}

.frisbuy-review-author__rating-simple {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #303133;
  white-space: nowrap;
  flex: 0 0 auto;
  min-width: fit-content;

  svg {
    fill: #212121;
    width: 24px;
    height: 24px;
    min-width: 24px;
  }
}

.frisbuy-review-author__rating-value {
  display: inline-block;
  min-width: max-content;
  font-size: 18px;
  line-height: 20px;
  font-weight: 400;
  color: #212121;
  opacity: 1;
  visibility: visible;
}

.frisbuy-review-author__rating-mobile-stars {
  display: none;
}

@media (max-width: 1024px) {
  .frisbuy-review-author--popup {
    .frisbuy-review-author__rating-simple {
      display: none;
    }

    .frisbuy-review-author__rating-mobile-stars {
      display: inline-flex;
      align-items: center;
    }
  }
}
</style>
